import { useMemo } from 'react';

import { checkSubscriptionExpired } from '../../../api/Subscription';
import ErrorIcon from '../../../assets/svg/error-icon-white.svg';
import InfoIcon from '../../../assets/svg/info-white.svg';
import warningIcon from '../../../assets/svg/warning-icon.svg';
import { useManageSubscription } from '../../../hooks';
import { calendarDaysDiff, formatDate, fromUnixTime, startOfUKDay } from '../../../utils/dateUtils';
import BasicSpinner from '../../BasicSpinner';
import InfoModal from '../../Modal/InfoModal';
import AlertBar from '../AlertBar';

interface SubscriptionExpiredAlertProps {
  /** Unix timestamp (seconds) — already includes the 7-day grace period from the backend. */
  subscriptionExpiry: number;
  /** Unix timestamp (seconds) — used only for the trial-end countdown banner. */
  renewalDate: number;
  isTrial?: boolean;
}

const SubscriptionExpiredAlert: React.FC<SubscriptionExpiredAlertProps> = ({
  subscriptionExpiry,
  renewalDate,
  isTrial,
}) => {
  const { redirecting, errorMessage, setErrorMessage, onManageSubscription } = useManageSubscription();
  const isExpired = useMemo(() => checkSubscriptionExpired(subscriptionExpiry), [subscriptionExpiry]);

  const trialDaysLeft = useMemo(() => {
    const now = startOfUKDay();
    const trialEnd = startOfUKDay(fromUnixTime(renewalDate));
    return calendarDaysDiff(trialEnd, now);
  }, [renewalDate]);

  const isTrialEndingSoon = useMemo(
    () => isTrial === true && trialDaysLeft >= 0 && trialDaysLeft <= 7,
    [isTrial, trialDaysLeft]
  );

  const modalContent = useMemo(
    () => (
      <div>
        <div className="text-center mb-3">
          <img src={warningIcon} alt="Warning icon" />
        </div>
        <div className="text-center mb-3">Something went wrong: {errorMessage}</div>
      </div>
    ),
    [errorMessage]
  );

  // Banner logic:
  // - subscription_expiry elapsed: show the hard "expired" banner.
  // - Trial (price 0): show the countdown banner in the last 7 days before renewalDate.
  // - Anything else: no banner.
  if (!isExpired && !isTrialEndingSoon) return null;

  let message;
  let icon = ErrorIcon;
  let variant: string = 'danger';

  if (isExpired) {
    message = (
      <>
        Your subscription has expired. We've stopped optimising your battery. <u>Resubscribe here</u>.
      </>
    );
  } else {
    if (trialDaysLeft === 0) {
      message = (
        <>
          Free Trial ends today on {formatDate(fromUnixTime(renewalDate), 'do MMMM yyyy')} and you will automatically
          move to a paid subscription.
        </>
      );
    } else {
      const dayWord = trialDaysLeft === 1 ? 'day' : 'days';
      message = (
        <>
          Free Trial ends in {trialDaysLeft} {dayWord} on {formatDate(fromUnixTime(renewalDate), 'do MMMM yyyy')} and
          you will automatically move to a paid subscription.
        </>
      );
    }
    icon = InfoIcon;
    variant = 'dark';
  }

  return (
    <>
      <InfoModal show={!!errorMessage} onClose={() => setErrorMessage(undefined)} infoContent={modalContent} />
      <AlertBar
        label={
          <div>
            {message}
            {redirecting && <BasicSpinner color="white" />}
          </div>
        }
        icon={icon}
        iconSize={20}
        onClick={onManageSubscription}
        variant={variant}
      />
    </>
  );
};

export default SubscriptionExpiredAlert;
